import { CONFLUENT_ALL_STAR_MAPPINGS } from "@/configuration/groups/confluent-all-star/document-mappings";
import {
  CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS,
  getConfluentAllStarEmailRecipients,
} from "@/configuration/groups/confluent-all-star/routing";
import { DocRoutingStrategy } from "@/src/lib/constants";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { Classification, ClassificationSection } from "@/src/models/constants/classifications";
import { DefaultWorkflow, Workflow, WorkflowType } from "@/src/models/constants/group";
import { Patient } from "@/src/models/patient";
import { DEFAULT_CLASSIFICATIONS_TO_EXTRACT, Section } from "@/src/models/section";

// Re-exported so configuration/index.ts imports Confluent doc-routing helpers from one module.
export {
  CONFLUENT_ALL_STAR_TIMEZONE,
  getConfluentAllStarDocRoutingEmailSubject,
} from "@/configuration/groups/confluent-all-star/routing";

export function getConfluentAllStarHolidays(): HolidayNames[] {
  return [HolidayNames.THANKSGIVING, HolidayNames.CHRISTMAS_DAY, HolidayNames.NEW_YEARS_DAY];
}

const CONFLUENT_ALL_STAR_CLASSIFICATIONS: Classification[] = Array.from(
  new Set<Classification>([
    ...DEFAULT_CLASSIFICATIONS_TO_EXTRACT,
    ...(Object.keys(CONFLUENT_ALL_STAR_MAPPINGS) as Classification[]),
  ])
);

const CONFLUENT_ALL_STAR_NON_EXTRACT_CLASSIFICATIONS = new Set<Classification>([
  Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT,
]);

export function getConfluentAllStarClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classifications",
      options: [...CONFLUENT_ALL_STAR_CLASSIFICATIONS],
    },
  ];
}

export function getConfluentAllStarClassificationToExtract(): Classification[] {
  return CONFLUENT_ALL_STAR_CLASSIFICATIONS.filter(
    (classification) => !CONFLUENT_ALL_STAR_NON_EXTRACT_CLASSIFICATIONS.has(classification)
  );
}

export function getConfluentAllStarClassificationForDocumentUpload(): Classification[] {
  return CONFLUENT_ALL_STAR_CLASSIFICATIONS;
}

export function getConfluentAllStarWorkflows(): Partial<Record<WorkflowType, Classification[]>> {
  const referralClassifications = DefaultWorkflow[Workflow.REFERRAL] ?? [];

  return {
    ...DefaultWorkflow,
    // Junk is extracted so it surfaces for intake review (rather than silently auto-closing as a
    // no-extract section), but it belongs to no downstream workflow: after intake it is dropped.
    [Workflow.DOC_MANAGEMENT]: getConfluentAllStarClassificationToExtract().filter(
      (classification) => !referralClassifications.includes(classification) && classification !== Classification.JUNK
    ),
  };
}

export function getConfluentAllStarDocRoutingStrategy(section: Section, _patient: Patient): DocRoutingStrategy[] {
  const classification = section.uploadClassification || section.classification;

  if (classification === Classification.JUNK || classification === Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT) {
    return [];
  }

  if (CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS.has(classification)) {
    return [DocRoutingStrategy.EMAIL];
  }

  return [DocRoutingStrategy.EHR];
}

export function getConfluentAllStarDocRoutingEmailRecipients(section: Section, _patient: Patient): string[] {
  return getConfluentAllStarEmailRecipients(section);
}

const CONFLUENT_ALL_STAR_PATIENTLESS_CLASSIFICATIONS = new Set<Classification>([
  Classification.JUNK,
  Classification.ADMIN,
  Classification.ADMIN_CREDENTIAL,
]);

export function isConfluentAllStarPatientlessDoc(section: Section): boolean {
  return CONFLUENT_ALL_STAR_PATIENTLESS_CLASSIFICATIONS.has(section.uploadClassification || section.classification);
}

export function shouldConfluentAllStarForceIntakeReview(): boolean {
  // Every ingested fax must surface a review-needed intake task — no exceptions. Forcing review
  // disables the deterministic "disposition" auto-close, so documents with no extractable
  // classification (e.g. entirely INCOMPLETE_CANNOT_LINK_TO_PATIENT, or a BLANK/UNKNOWN
  // misclassification) escalate to a human instead of silently completing.
  return true;
}
